import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Breadcrumbs,
  Link,
  Tooltip
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { getMenus, createMenu, updateMenu, deleteMenu, getMenuSchema, appApi } from '../services/api';
import { MenuV2, MenuItem as MenuItemType, App } from '../models';
import Form from "@rjsf/mui";
import { RJSFSchema } from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";
import { IChangeEvent } from '@rjsf/core';
import '../App.css'; // Import the CSS for menu styling
import FileUrlWidget from '../components/widgets/FileUrlWidget';

const MenuManagement: React.FC = () => {
  const [menus, setMenus] = useState<MenuV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentMenu, setCurrentMenu] = useState<Partial<MenuV2> | null>(null);
  const [apps, setApps] = useState<App[]>([]);
  const [menuSchema, setMenuSchema] = useState<RJSFSchema | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  useEffect(() => {
    fetchMenus();
    fetchApps();
    fetchMenuSchema();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMenus = async () => {
    try {
      setLoading(true);
      const data = await getMenus();
      setMenus(Array.isArray(data.results) ? data.results : data);
    } catch (error) {
      console.error('Error fetching menus:', error);
      showNotification('Failed to load menus', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchApps = async () => {
    try {
      const data = await appApi.getAll();
      setApps(Array.isArray(data.results) ? data.results : data);
    } catch (error) {
      console.error('Error fetching apps:', error);
      showNotification('Failed to load apps', 'error');
    }
  };

  const fetchMenuSchema = async () => {
    try {
      setSchemaLoading(true);
      const schema = await getMenuSchema();
      setMenuSchema(schema);
    } catch (error) {
      console.error('Error fetching menu schema:', error);
      showNotification('Failed to load menu schema', 'error');
    } finally {
      setSchemaLoading(false);
    }
  };

  const handleEditMenu = (menu: MenuV2) => {
    setCurrentMenu({ ...menu });
    setIsEditing(true);
  };

  const handleCreateNew = () => {
    setCurrentMenu({ name: '', is_active: true, items: [] });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!currentMenu) return;

    // Show validation before saving
    setShowValidation(true);

    try {
      setSaving(true);
      if (currentMenu.id) {
        await updateMenu(currentMenu.id, currentMenu);
        showNotification('Menu updated successfully', 'success');
      } else {
        await createMenu(currentMenu);
        showNotification('Menu created successfully', 'success');
      }
      fetchMenus();
      handleCancel(); // Return to menu list
    } catch (error) {
      console.error('Error saving menu:', error);
      showNotification('Error saving menu', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this menu?')) {
      try {
        await deleteMenu(id);
        showNotification('Menu deleted successfully', 'success');
        fetchMenus();
        if (currentMenu?.id === id) {
          handleCancel(); // Return to menu list if currently editing the deleted menu
        }
      } catch (error) {
        console.error('Error deleting menu:', error);
        showNotification('Error deleting menu', 'error');
      }
    }
  };

  const handleMenuItemsChange = (e: IChangeEvent<MenuItemType[]>) => {
    if (currentMenu) {
      setCurrentMenu({ ...currentMenu, items: e.formData });
    }
  };

  const handleCancel = () => {
    setCurrentMenu(null);
    setIsEditing(false);
    setShowValidation(false);
  };

  const showNotification = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading && !currentMenu) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // Main menu list view
  if (!isEditing || !currentMenu) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Menu Management</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Create New Menu
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>App</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {menus.map((menu) => (
                  <TableRow key={menu.id} hover>
                    <TableCell>{menu.id}</TableCell>
                    <TableCell>{menu.name}</TableCell>
                    <TableCell>
                      {menu.app 
                        ? apps.find(a => a.id === menu.app)?.name || menu.app 
                        : 'None'}
                    </TableCell>
                    <TableCell>
                      <Box 
                        component="span"
                        sx={{ 
                          color: menu.is_active ? 'success.main' : 'text.secondary',
                          fontWeight: menu.is_active ? 'medium' : 'normal'
                        }}
                      >
                        {menu.is_active ? 'Active' : 'Inactive'}
                      </Box>
                    </TableCell>
                    <TableCell>{menu.items?.length || 0} items</TableCell>
                    <TableCell>
                      <Tooltip title="Edit Menu">
                        <IconButton size="small" onClick={() => handleEditMenu(menu)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Menu">
                        <IconButton size="small" onClick={() => handleDelete(menu.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {menus.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body1" sx={{ py: 2 }}>
                        No menus found. Create your first menu!
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    );
  }

  // Menu Edit View
  return (
    <Box sx={{ pb: 4 }}>
      <Box mb={3}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            component="button"
            onClick={handleCancel}
            sx={{ 
              textDecoration: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Menu Management
          </Link>
          <Typography color="text.primary">
            {currentMenu.id ? `Edit Menu: ${currentMenu.name}` : 'Create New Menu'}
          </Typography>
        </Breadcrumbs>
      </Box>

      <Card elevation={2} sx={{ mb: 2 }}>
        <CardHeader 
          title={
            <Box display="flex" alignItems="center">
              <Typography variant="h5" component="div" mr={1}>
                {currentMenu.id ? `Edit Menu: ${currentMenu.name}` : 'Create New Menu'}
              </Typography>
              {saving && <CircularProgress size={20} sx={{ ml: 2 }} />}
            </Box>
          } 
          action={
            <Box>
              <Button 
                variant="outlined" 
                onClick={handleCancel} 
                startIcon={<ArrowBackIcon />}
                sx={{ mr: 1 }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSave}
                startIcon={<SaveIcon />}
                disabled={saving}
              >
                Save
              </Button>
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }} elevation={0} variant="outlined">
                <Typography variant="h6" gutterBottom>Menu Settings</Typography>
                <TextField
                  fullWidth
                  label="Menu Name"
                  value={currentMenu.name || ''}
                  onChange={(e) => setCurrentMenu({ ...currentMenu, name: e.target.value })}
                  margin="normal"
                  required
                />
                
                <FormControl fullWidth margin="normal">
                  <InputLabel>App</InputLabel>
                  <Select
                    value={currentMenu.app || ''}
                    onChange={(e) => 
                      setCurrentMenu({ 
                        ...currentMenu, 
                        app: e.target.value ? e.target.value : null 
                      })
                    }
                    label="App"
                  >
                    <MenuItem value="">None</MenuItem>
                    {apps.map((app) => (
                      <MenuItem key={app.id} value={app.id}>
                        {app.name} ({app.type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Box sx={{ mt: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentMenu.is_active || false}
                        onChange={(e) =>
                          setCurrentMenu({ ...currentMenu, is_active: e.target.checked })
                        }
                      />
                    }
                    label={
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: currentMenu.is_active ? 'success.main' : 'text.secondary',
                          fontWeight: 'medium'
                        }}
                      >
                        {currentMenu.is_active ? 'Active' : 'Inactive'}
                      </Typography>
                    }
                  />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 2 }} elevation={0} variant="outlined">
                <Typography variant="h6" gutterBottom>Menu Items</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Configure the menu items structure below. You can add links or submenus with nested links.
                </Typography>

                {schemaLoading ? (
                  <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress size={24} />
                  </Box>
                ) : menuSchema ? (
                  <Box className="menu-editor-container">
                    <Form
                      schema={menuSchema}
                      formData={currentMenu.items || []}
                      validator={validator}
                      onChange={handleMenuItemsChange}
                      liveValidate={showValidation}
                      noHtml5Validate
                      disabled={false}
                      widgets={{
                        FileUrlWidget: FileUrlWidget,
                        'file-url': FileUrlWidget // Add explicit format mapping
                      }}
                      uiSchema={{
                        'ui:rootFieldId': 'menu',
                        'ui:submitButtonOptions': {
                          norender: true,
                        },
                        '*': {
                          classNames: 'menu-item-field'
                        },
                        'items': {
                          'items': {
                            'type': {
                              'ui:widget': 'hidden'
                            },
                            'icon': {
                              'ui:widget': 'FileUrlWidget',
                              'ui:options': {
                                fileType: 'image'
                              }
                            },
                            'items': {
                              'items': {
                                'icon': {
                                  'ui:widget': 'FileUrlWidget',
                                  'ui:options': {
                                    fileType: 'image'
                                  }
                                }
                              }
                            }
                          }
                        }
                      }}
                    />
                  </Box>
                ) : (
                  <Typography color="error">Failed to load menu schema</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Box mt={3} display="flex" justifyContent="flex-end">
        <Button 
          variant="outlined" 
          onClick={handleCancel} 
          sx={{ mr: 2 }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSave}
          startIcon={<SaveIcon />}
          disabled={saving}
        >
          Save Menu
        </Button>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MenuManagement;
